export type ReservationTiming = { reservationAllDay?: boolean; eventTime?: string; reservationEndTime?: string };
function minutes(time: string | undefined, end = false) {
  if (end && (time === "24:00" || time === "00:00")) return 1440;
  if (!time || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) return null;
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
}
/** Legacy bookings remain all-day until explicitly edited into a timed reservation. */
export function reservationInterval(row: ReservationTiming): [number, number] {
  if (row.reservationAllDay !== false) return [0, 1440];
  const start = minutes(row.eventTime), end = minutes(row.reservationEndTime, true);
  return start !== null && end !== null && end > start ? [start, end] : [0, 1440];
}
export function reservationTiming(allDay = true, start?: string, end?: string) {
  if (allDay) return { reservationAllDay: true, eventTime: undefined, reservationEndTime: undefined, durationMinutes: undefined };
  const a = minutes(start), b = minutes(end, true);
  if (a === null || b === null || b <= a) throw new Error("Choose a start and end time on the same date, with the end after the start. Midnight as the end means the end of that day.");
  return { reservationAllDay: false, eventTime: start!, reservationEndTime: b === 1440 ? "24:00" : end!, durationMinutes: b - a };
}
/**
 * Display-only: "18:30" -> "6:30 PM", "00:05" -> "12:05 AM", "12:00" -> "12:00 PM".
 * Stored values, args and comparisons stay 24h "HH:mm"; only call this when
 * rendering text for staff or guests. Unparseable input is returned unchanged
 * so a bad row never renders as a fabricated time.
 */
export function formatTime12h(time: string | null | undefined): string {
  if (typeof time !== "string") return "";
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
  if (!match) return time;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 24 || minute > 59 || (hour === 24 && minute !== 0)) return time;
  const suffix = hour >= 12 && hour < 24 ? "PM" : "AM";
  return `${hour % 12 || 12}:${match[2]} ${suffix}`;
}

export function reservationTimeLabel(row: ReservationTiming) {
  if (row.reservationAllDay !== false) return "All day";
  const display = (value?: string) => {
    if (value === "24:00") return "midnight (end of day)";
    return formatTime12h(value ?? "00:00");
  };
  return `${display(row.eventTime)}–${display(row.reservationEndTime)}`;
}
